import { useContext, useRef } from "react";
import { useLocation } from "react-router-dom";

import { handleArchiveConfirm, handleHiddenChange } from "@app/home/Helpers";
import { useNpmInvalidateQueries } from "@app/network-portfolio-management/services/npmQueries";
import {
  NetworkPortfolioManagementProject,
  NetworkPortfolioManagementProjectStatus,
  NetworkPortfolioManagementProjectTypeEnum,
} from "@app/network-portfolio-management/interfaces";
import ProjectService from "@app/projects/projectService";
import ZipProjectService from "@app/projects/zipProjectService";
import { SharedProjectActionMenuProps } from "@app/projects/network-assessment/SharedProjectActionMenu";
import { ProjectActionMenuModel, buildShareState } from "@app/projects/network-assessment/projectActionMenuTypes";
import { ResourceType } from "@app/resources";
import { UserContext } from "@app/user/UserContext";

/**
 * Maps a Network Portfolio Management project into the lean, view-agnostic model the shared action menu
 * consumes. Archived state is derived from the project's archived date (the dashboard's archived view renders
 * these same cards/rows), which flips the menu item to "Unarchive". isReadOnly mirrors the running (Processing)
 * state so the model is hydrated correctly; queued state is not surfaced by the NPM list and is defaulted.
 */
export const toActionMenuModel = (project: NetworkPortfolioManagementProject): ProjectActionMenuModel => ({
  dataReady: true,
  isArchived: project.dateArchived != null,
  isPublishable: project.status === NetworkPortfolioManagementProjectStatus.PublishPending,
  isQueued: false,
  isReadOnly: project.status === NetworkPortfolioManagementProjectStatus.Running,
  isRunning: project.status === NetworkPortfolioManagementProjectStatus.Running,
  isZipAnalysis: project.projectType === NetworkPortfolioManagementProjectTypeEnum.ZipCodeNetworkAssessment,
  name: project.name,
  projectId: project.projectId,
});

/**
 * Resolves the user, router location and react-query client, maps an NPM project into the shared action
 * menu's contract, and wires the action callbacks to invalidate the dashboard's project query so the list
 * refreshes after an action. ProjectCard spreads the result straight into
 * SharedProjectActionMenu, so there is no per-view wrapper component to keep in sync.
 *
 * Owned projects (not shared to the user) get the full owner menu; shared projects get the viewer menu
 * (Project Options view-only plus Hide/Unhide). showingHidden is derived from the project's own isHidden
 * flag, the same "derive from data, not from view" pattern toActionMenuModel uses for isArchived, so the
 * label is correct however the project is currently being viewed.
 */
export const useNpmActionMenuProps = (project: NetworkPortfolioManagementProject): SharedProjectActionMenuProps => {
  const user = useContext(UserContext);
  const { pathname } = useLocation();
  const projectService = useRef(new ProjectService()).current;
  const zipProjectService = useRef(new ZipProjectService()).current;

  // Refresh every project-list view (ids, paged data, grouped, sorted) after an action so the
  // grouped/sorted dashboards stay consistent with the flat list — see useNpmInvalidateQueries.
  // Also invalidates the gap-by-county-status breakdown (publishing/updating project options can
  // change it too), so this one hook stays the single place every action refreshes through.
  const invalidateProjects = useNpmInvalidateQueries();

  const model = toActionMenuModel(project);

  const handlePublish = async (comment: string): Promise<void> => {
    if (model.isZipAnalysis) {
      await zipProjectService.publishZipProject(project.projectId, comment);
    } else {
      await projectService.publishProject(project.projectId, comment);
    }
    await invalidateProjects();
  };

  return {
    callbacks: {
      // handleArchiveConfirm's first arg is the project's *current* archived state: true ⇒ unarchive (set Active),
      // false ⇒ archive. Derive it from the project so the archived view actually unarchives (AC #4).
      onArchiveConfirm: () => handleArchiveConfirm(project.dateArchived != null, project.projectId, () => { invalidateProjects(); }),
      onPublish: handlePublish,
      onUpdateProjectOptions: invalidateProjects,
    },
    model,
    onHidden: () => handleHiddenChange(project.projectId, () => { invalidateProjects(); }),
    projectOwned: !project.isShared,
    setProjectIsReadOnly: () => { invalidateProjects(); },
    shareLocationState: buildShareState(project.name, project.projectId, [ResourceType.Project], pathname),
    showingHidden: project.isHidden,
    user,
  };
};
